import { Endpoints } from "src/config/endpoints.config";
import { Injectable } from "@angular/core";
import { AuthenticationService } from "../authentication.service";
import { decrypt, encrypt } from "../crypto";
import { CRMInstance } from "@omni/classes/authentication/instance.class";
import { DiskService } from "../disk/disk.service";
import { AuthenticationDataService } from "@omni/data-services/authentication/authentication.service";
// import { AppInsightsService } from "@markpieszak/ng-application-insights";
import { BrowserCredential } from "./browser.credential";
import { RepServices } from "@omni/data-services/rep/rep.services";
import { DeviceService } from "../device/device.service";
import { LaunchDarklyProvider } from "@omni/providers/launch-darkly/launch-darkly";
import { GlobalUtilityService } from "../global-utility.service";
import { LOGGED_OUT, LOGGED_OUT_AND_BACK_TO_LOGIN, REP_STATUS } from "@omni/models/rep-status-model";
import { TokenCredential, TokenCredentialOptions } from "./token.credential";
import { ElectronCredential } from "./electron.credential";
import { CordovaCredential, IntuneManager } from "./cordova.credential";
import { FeatureActionsMap, User } from "@omni/classes/authentication/user.class";
import { CordovaPlugin } from "../cordova";
import { FeatureActionsService } from "../feature-actions/feature-actions.service";
import { Utility } from "@omni/utility/util";
import { UserConfig } from "@omni/classes/authentication/user-config.class";
import { TranslateService } from "@ngx-translate/core";
import { AlertService } from "../alert/alert.service";
import { RecordingStore } from '@omni/services/speech/recording-store';

interface AuthConfig {
  tenant: string;
  region: string;
  clientId: string;
  dynamicsAdminCentreUrl: string;
  instanceUrl?: string;
  userId: string;
  domain: string;
  redirectUri: string;
}

function defaultScopeForUrl(url: string) {
  return `${url}${url.endsWith('/') ? '' : '/'}.default`
}

@Injectable({
  providedIn: 'root'
})
export class LoginService {
  private config: AuthConfig;
  private _ready: Promise<void>;
  private storage = sessionStorage;
  private didJustLogoutWithOfflineStateAndRemeberMe = false;
  private _cordovaHelpers = new CordovaPlugin('CordovaHelpers');

  public rememberMe = false;
  get username() {
    return this.config?.userId;
  }

  constructor(
    private readonly authenticationService: AuthenticationService,
    private readonly authService: AuthenticationDataService,
    private readonly repService: RepServices,
    // private readonly appInsightsService: AppInsightsService,
    private readonly device: DeviceService,
    private readonly launchDarkly: LaunchDarklyProvider,
    private readonly guv: GlobalUtilityService,
    private readonly disk: DiskService,
    private faService: FeatureActionsService,
    private readonly translate: TranslateService,
    private readonly alertService: AlertService,
    private readonly recordings: RecordingStore,
  ) {
  }

  async ready() {
    if (!this._ready) {
      this._ready = this.readCache();
    }
    return this._ready;
  }

  get isAuthenticated() {
    return !!(this.authenticationService.user && this.authenticationService.userConfig)
  }

  private async readCache() {
    try {
      this.rememberMe = false;
      let domain = this.storage.getItem('omni.adal.domain');
      let configJson = domain && this.storage.getItem(domain);
      console.log('[RememberMe:readCache] storage=' + (this.storage === localStorage ? 'localStorage' : 'sessionStorage') + ', domain=' + !!domain + ', configJson=' + !!configJson);
      if (!domain && !configJson && this.storage != localStorage) {
        this.storage = localStorage;
        return this.readCache();
      }
      let config = configJson && (JSON.parse(configJson) as AuthConfig);
      if (config && config.clientId && config.domain && config.tenant && config.dynamicsAdminCentreUrl && config.userId) {
        let rememberMe = this.storage === localStorage;
        console.log('[RememberMe:readCache] config found, userId=' + config.userId + ', rememberMe=' + rememberMe + ', instanceUrl=' + !!config.instanceUrl);
        const credential = await this.createCredential({
          clientId: config.clientId,
          username: config.userId,
          domain: config.domain,
          redirectUri: config.redirectUri,
          authority: this.getAuthorityUrl(config.tenant, config.region || (config.domain.endsWith('.cn') ? 'china' : '')),
          persist: rememberMe,
        });
        this.authService.credential = credential;
        this.rememberMe = rememberMe;
        this.config = config;
        // this.appInsightsService.config = {
        //   accountId: this.config.tenant
        // };
        if (this.didJustLogoutAndBackToLogin()) {
          this.clearLogoutAndBackToLoginFlag()
          this.didJustLogoutWithOfflineStateAndRemeberMe = true;
        } else if (!this.didLogout() || (this.didLogout() && this.repService.wasLastUserStateOffline())) {
          await this.restoreSession(true);
        }
      }
    } catch (ex) {
      console.error('[RememberMe:readCache] exception caught, rememberMe=' + this.rememberMe, ex);
      // Preserve config when rememberMe is active so the login page can still show the stored username
      if (!this.rememberMe) {
        this.config = undefined;
      }
    }
  }

  private async createCredential(options: TokenCredentialOptions){
    let credential: TokenCredential = !this.device.isNativeApp
      ? new BrowserCredential(options)
      : this.device.deviceFlags.electron
        ? new CordovaCredential(options, this.translate, this.alertService)
        : new CordovaCredential(options, this.translate, this.alertService);
    await credential.ready();
    if (this.device.deviceFlags.nativeIOS || this.device.deviceFlags.nativeAndroid) {
      await IntuneManager.initIntune(options.clientId);
    }
    return credential;
  }

  private async saveCache() {
    let config = this.config;
    if (!config.clientId || !config.domain || !config.tenant || !config.dynamicsAdminCentreUrl) {
      return;
    }
    this.storage = this.rememberMe ? localStorage : sessionStorage;
    this.storage.setItem('omni.adal.domain', config.domain);
    this.storage.setItem(config.domain, JSON.stringify(config));
    if (!this.rememberMe) {
      let domain = localStorage.getItem('omni.adal.domain');
      if (domain) {
        localStorage.removeItem('omni.adal.domain');
        localStorage.removeItem(domain);
      }
    }
  }

  async restoreSession(force = false) {
    console.log('[RememberMe:restoreSession] called, force=' + force + ', hasConfig=' + !!this.config);
    if (!this.config) return false;
    this.authService.isOfflineLoginFlow = false;
    const deviceOffline = !(await this.device.checkNetworkConnectivity()) || this.device.isOffline;
    const repStateOffline = this.repService.wasLastUserStateOffline();
    console.log('[RememberMe:restoreSession] deviceOffline=' + deviceOffline + ', repStateOffline=' + repStateOffline);

    if (force || repStateOffline || deviceOffline) {
      if (repStateOffline) {
        this.device.isOffline = this.device.isUserStateOffline = this.repService.isOfflineState = true;
      } else if (this.device.isDeviceRealOffline) {
        this.device.isOffline = this.repService.isOfflineState = true;
      }
      if (!repStateOffline && !deviceOffline) {
        this.authenticationService.shouldTrySyncingDuringAppLaunch = true;
      }
      this.device._isDeviceOfflineFromLogin = true;
      this.authService.isOfflineLoginFlow = repStateOffline || deviceOffline || this.didJustLogoutWithOfflineStateAndRemeberMe;
    } else {
      return false;
    }
    if (!this.device.isOffline && this.authService.credential) {
      await this.authService.getTokenForSelectedInstance();
    }

    const key = this.authenticationService.getOfflineLoginKey(this.config.userId);
    console.log('[RememberMe:restoreSession] offlineLoginKey=' + key);
    // If browser, decrypt and restore from localStorage
    if (this.device.isNativeApp && this.device.deviceFlags.electron) {
      const iv = localStorage.getItem(key);
      const encBuffer = localStorage.getItem(key + '-encBuffer');
      console.log('[RememberMe:restoreSession] electron: iv=' + !!iv + ', encBuffer=' + !!encBuffer);

      if (iv && encBuffer) {
        // Decrypt time
        const results = await decrypt(encBuffer, iv, key);
        const loginConfiguration = results && JSON.parse(results);
        if (loginConfiguration && loginConfiguration.user) {
          this.authenticationService.user = User.fromStoredObject(loginConfiguration.user);
          this.authenticationService.userConfig = loginConfiguration.userConfig;

          // Restore FA values
          this.faService.updateFaValues();
        }
      }
    } else {
      // If hybrid app, decrypt and restore from secure storage
      const iv = await this.disk.getFromSecureStorage(key);
      const encBuffer = await this.disk.getFromSecureStorage(key + '-encBuffer');
      console.log('[RememberMe:restoreSession] secureStorage: iv=' + !!iv + ', encBuffer=' + !!encBuffer);

      if (iv && encBuffer) {
        //Decrypt time
        const results = await decrypt(encBuffer, iv, key);
        const loginConfiguration = results && JSON.parse(results);
        if (loginConfiguration && loginConfiguration.user) {
          this.authenticationService.user = User.fromStoredObject(loginConfiguration.user);
          this.authenticationService.userConfig = loginConfiguration.userConfig;

          // Restore FA values
          this.faService.updateFaValues();
        }
      }
    }

    if (this.authenticationService.user && this.authenticationService.userConfig) {
      console.log('[RememberMe:restoreSession] offline session restored successfully');
      await this.afterLoggedIn(true)
      return true;
    } else {
      console.log('[RememberMe:restoreSession] offline session NOT found, instanceUrl=' + !!this.config?.instanceUrl);
      if (!this.config?.instanceUrl) return false;
      let scope = defaultScopeForUrl(this.config.dynamicsAdminCentreUrl);
      console.log('[RememberMe:restoreSession] attempting getToken for scope=' + scope);
      let authResult = await this.authService.credential.getToken(scope);
      console.log('[RememberMe:restoreSession] getToken succeeded, calling fetchInstances');
      await this.fetchInstances(authResult.token);
      let selectedInstance = this.authenticationService.userConfig?.CRMInstances?.find(instance => instance.url.toLowerCase() == this.config.instanceUrl.toLowerCase())
      if (selectedInstance) {
        await this.selectInstance(selectedInstance);
        return this.isAuthenticated;
      }
    }
    return false;
  }

  async login(username: string, rememberMe: boolean) {
    const domain = username.split('@')[1];
    let domainDetails = await this.getDomainDetails(domain)
    if (!domainDetails) {
      throw new Error("INVALID_EMAIL_MESSAGE")
    }
    let config: AuthConfig = {
      tenant: domainDetails.tenantId,
      clientId: domainDetails.clientId,
      dynamicsAdminCentreUrl: domainDetails.dynamicsAdminCentreUrl,
      redirectUri: window.location.origin + '/.auth/login/aad/callback',
      userId: username,
      region: domainDetails.region,
      domain
    };
    localStorage.setItem('region', domainDetails.region);
    let credential = await this.createCredential({
      clientId: config.clientId,
      redirectUri: config.redirectUri,
      username,
      domain,
      authority: this.getAuthorityUrl(config.tenant, config.region),
      persist: rememberMe,
    });
    let scope = defaultScopeForUrl(config.dynamicsAdminCentreUrl);
    // Give a delay to have the credential instance ready  OMNI-44913
    await Utility.delay(1000);
    let authResult = await credential.getToken(scope);
    if (authResult) {
      if (this.device.deviceFlags.nativeIOS || this.device.deviceFlags.nativeAndroid) {
        await IntuneManager.registerAccount(config.clientId, config.userId).catch(() => {});
      }
      await this.fetchInstances(authResult.token)
      this.authService.credential = credential;
      this.config = config;
      this.rememberMe = rememberMe;
      // this.appInsightsService.config = {
      //   accountId: this.config.tenant
      // };
      this.saveCache();
      this.clearLogoutFlag();
      this.authenticationService.shouldTrySyncingDuringAppLaunch = false;
      return true;
    }
    return false;
  }

  private async fetchInstances(token: string) {
    try {
      // const url: string = Endpoints.authentication.GET_INSTANCES;
      const url: string = (localStorage.getItem("region") && localStorage.getItem("region") === "China") ? Endpoints.authentication.GET_CHINA_INSTANCES : Endpoints.authentication.GET_INSTANCES;
      let res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        this.authenticationService.mapLoginResponse(data);
      }
    } catch (fetchErr) {
      console.error('[RememberMe:fetchInstances] network fetch failed', fetchErr);
      throw new Error("LOGIN_NO_NET_CONNECTIVITY");
    } finally {
      if (!this.authenticationService.userConfig?.CRMInstances?.length) {
        console.warn('[RememberMe:fetchInstances] no CRMInstances — this error will override any prior throw');
        throw new Error("LOGIN_NO_OMNI_INSTANCE_ASSIGNED")
      }
    }
  }

  private getAuthorityUrl(tenant: string, region: string) {
    let loginPartner = Endpoints.authentication.MICROSOFT_BASE_URL_GLOBAL;
    region = region?.toLowerCase();
    if (region == 'china') {
      loginPartner = Endpoints.authentication.MICROSOFT_BASE_URL_CHINA;
    }
    return `${loginPartner}/${tenant}`;
  }


  private async getDomainDetails(domain) {
    // return {
    //   tenantId: 'common',
    //   clientId: 'd78440c4-622c-496f-ba35-6250243d9a9a',
    //   dynamicsAdminCentreUrl: 'https://globaldisco.crm.dynamics.com/'
    // }
    let url = Endpoints.domain.GET_DOMAIN_DETAILS + domain;
    let res = await fetch(url).catch(() => { throw new Error("LOGIN_NO_NET_CONNECTIVITY") });
    if (res.ok) {
      let json = await res.json();;
      return {
        tenantId: json['realm-id'] as string,
        clientId: json['client-id'] as string,
        region: json.region as string || (domain.endsWith('.cn') ? 'china' : ''),
        dynamicsAdminCentreUrl: json.dynamicsAdminCentreUrl as string
      };
    } else {
      let data = await res.json();
      throw new class extends Error {
        readonly error = data;
        readonly status = res.status;
        constructor() {
          super(data.errorMessage || data.toString())
        }
      };
    }
  }

  async selectInstance(instance: CRMInstance) {
    let instanceUrl = instance.url;
    let authResult = await this.authService.getTokenForResource(instanceUrl);
    if (authResult) {
      this.authenticationService.userConfig.activeInstance = instance;
      await this.authService.getUser(true);
      await this.fetchTeamsConfig();
      await this.saveSession();
      await this.afterLoggedIn();
      return true;
    }
    return false;
  }

  private async fetchTeamsConfig() {
    if (this.authenticationService.hasFeatureAction(FeatureActionsMap.TEAMS_MEETING)) {
      let url = Endpoints.domain.GET_TEAMS_CONFIG;
      url = url.replace('{tenantid}', this.config.tenant);
      url = url.replace('{clientid}', this.config.clientId);
      let token = await this.authService.getTokenForSelectedInstance();
      let res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token.token}`
        }
      });
      if (res.ok) {
        this.authenticationService.userConfig.teamsConfig = await res.json();
      }
    }
  }

  private async afterLoggedIn(offlineMode = false) {
    this.clearLogoutFlag();
    // this.appInsightsService.config = {
    //   accountId: this.config.tenant
    // };
    await this.disk.setUserDB(this.authenticationService.userConfig);
    if (this.device.deviceFlags.native && (this.device.deviceFlags.ios || this.device.deviceFlags.android)) {
      this._cordovaHelpers.exec('setAppCenterUserId', this.authenticationService.user?.userPrincipalName ?? this.authenticationService.user?.displayName);
    }
    const hasNetwork = await this.device.checkNetworkConnectivity()
    await Promise.all([
      (async () => {
        if (hasNetwork) {
          this.launchDarkly.initialize().catch(() => { });
          this.guv.setupSessionStack();
        }
      })(),
      this.authService.fetchNotesAssistantConfiguration(offlineMode),
      this.authService.fetchOKIntegrationConfig(offlineMode)
    ]);
  }


  private async saveSession() {
    try {
      this.config.instanceUrl = this.authenticationService.userConfig.activeInstance.url
      await this.saveCache();
      if (!this.rememberMe) return;
      const username = this.config.userId;
      console.log('[RememberMe:saveSession] rememberMe=true, saving offline login info for ' + username);
      // If hybrid app, encrypt and save to secure storage
      const offlineConfiguration = {
        user: this.authenticationService.user,
        userConfig: this.authenticationService.userConfig,
      };
      this.authenticationService.saveOfflineLoginInfo(username, offlineConfiguration);
      console.log('[RememberMe:saveSession] saveOfflineLoginInfo fired (NOT awaited)');
      return true;
    }
    catch (ex) {
      console.error('[RememberMe:saveSession] exception', ex);
    }
    return false;
  }

  public async logout() {
    (navigator as any)?.splashscreen?.show();
    this.markLogout();
    this.markLogoutAndBackToLogin();
    let isOfflineState = this.repService.getCurrentUserState() === REP_STATUS.OFFLINE.userState;
    if (!isOfflineState || !this.rememberMe) {
      await this.disk.clearSecureStorage();
      // Recordings hold conversations and live outside secure storage, so they go
      // with the disk: leaving them keeps one rep's calls on a device the next rep
      // signs in to. Only on this path — a rep kept signed in offline keeps their
      // work, and their unread recordings are part of it.
      await this.recordings.clear();
      //Lets kill what we got on disk
      sessionStorage.clear();
      if (!this.rememberMe) {
        localStorage.clear();
      } else if (this.config.domain && localStorage.getItem(this.config.domain)) {
        this.config.instanceUrl = undefined;
        localStorage.setItem(this.config.domain, JSON.stringify(this.config));
      }
    }
    if (window.location.protocol.startsWith('file') && !window.location.href.endsWith('.html')) {
      window.location.href += `${!window.location.href.endsWith('/') ? '/' : ''}index.html`;
    } else {
      window.location.reload();
    }
  }

  private didLogout(): boolean {
    return window.localStorage.getItem(LOGGED_OUT) === 'true';
  }
  private markLogout() {
    if (this.device.isNativeApp && this.rememberMe) {
      window.localStorage.setItem(LOGGED_OUT, 'true');
    }
  }
  private clearLogoutFlag() {
    window.localStorage.removeItem(LOGGED_OUT);
  }

  private didJustLogoutAndBackToLogin(): boolean {
    return window.localStorage.getItem(LOGGED_OUT_AND_BACK_TO_LOGIN) === 'true';
  }
  private markLogoutAndBackToLogin() {
    if (this.rememberMe) {
      window.localStorage.setItem(LOGGED_OUT_AND_BACK_TO_LOGIN, 'true');
    }
  }
  private clearLogoutAndBackToLoginFlag() {
    window.localStorage.removeItem(LOGGED_OUT_AND_BACK_TO_LOGIN);
  }
}
